import { useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Card,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Divider,
    Stack,
    Tooltip,
    Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import BlockIcon from '@mui/icons-material/Block';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useDataProvider, useNotify } from 'react-admin';

import type { IrmDataProvider } from '../../providers/types';
import type { MyApiKey } from '../../types/types';
import { CreateApiKeyDialog } from '../admin/CreateApiKeyDialog';
import { canHoldKey } from '../admin/apiKeys';
import { formatDate, fullDate } from '../admin/keyDates';
import { KEEP_DIALOG_SIZE } from '../../root/touchTheme';
import type { KeyHolder } from '../admin/UserApiKeys';

import { partnerApiDocsUrl } from './apiDocsUrl';
import { createMyApiKey, revokeMyApiKey, useMyApiKeys } from './useMyApiKeys';

const ACTIVE_KEY_OWN =
    'You already have an active key. Revoke it first to issue a new one.';

const KeyRow = ({
    record,
    onRevoke,
    disabled,
}: {
    record: MyApiKey;
    onRevoke?: () => void;
    disabled?: boolean;
}) => {
    const active = record.status === 'active';
    return (
        <Box
            sx={{
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
                p: 2,
            }}
        >
            <Stack
                direction="row"
                flexWrap="wrap"
                alignItems="baseline"
                columnGap={1.5}
            >
                {/* The prefix, never the key — it is the only part of a
                    credential allowed to appear in a UI or a log line. */}
                <Typography component="code" sx={{ fontFamily: 'monospace' }}>
                    {record.prefix}…
                </Typography>
                {record.label && (
                    <Typography variant="body2" color="text.secondary">
                        “{record.label}”
                    </Typography>
                )}
            </Stack>
            <Stack
                direction="row"
                flexWrap="wrap"
                alignItems="center"
                justifyContent="space-between"
                columnGap={1.5}
                sx={{ mt: 1 }}
            >
                <Stack
                    direction="row"
                    flexWrap="wrap"
                    alignItems="center"
                    columnGap={1.5}
                >
                    <Chip
                        size="small"
                        label={record.status}
                        color={active ? 'success' : 'default'}
                        variant={active ? 'filled' : 'outlined'}
                    />
                    <Typography variant="caption" color="text.secondary">
                        {record.created_at ? (
                            <Tooltip title={fullDate(record.created_at)}>
                                <span>
                                    Created {formatDate(record.created_at)}
                                </span>
                            </Tooltip>
                        ) : (
                            // Keys minted before created_at was stored.
                            <span>Created date unknown</span>
                        )}
                        {' · '}
                        <Tooltip title={fullDate(record.last_used_at)}>
                            <span>
                                {record.last_used_at
                                    ? `Last used ${formatDate(record.last_used_at)}`
                                    : 'Never used'}
                            </span>
                        </Tooltip>
                    </Typography>
                </Stack>
                {active && onRevoke && (
                    <Button
                        size="small"
                        color="error"
                        startIcon={<BlockIcon />}
                        disabled={disabled}
                        onClick={onRevoke}
                    >
                        Revoke
                    </Button>
                )}
            </Stack>
        </Box>
    );
};

/**
 * The signed-in user's own partner API key, on the Settings page: the docs
 * link, create, see and revoke.
 *
 * Which of the four views shows is decided from the user record and the key
 * list. Eligible users get the whole card. A user who lost access but still
 * holds a key keeps the list and Revoke -- that is exactly when a key must be
 * killable -- but loses Create and the docs. Everyone else is told the feature
 * is not enabled. A list that is loading or failed never reads as "no keys".
 */
export const ApiAccessSection = ({ user }: { user: KeyHolder }) => {
    const notify = useNotify();
    const dataProvider = useDataProvider<IrmDataProvider>();
    const { keys, loading, error, refetch } = useMyApiKeys(user.id);
    const [creating, setCreating] = useState(false);
    const [revoking, setRevoking] = useState(false);
    // Revoking cannot be undone: nothing sets a key back to 'active' and the
    // raw key is gone, so one click is too little between a misclick and a
    // dead integration.
    const [confirming, setConfirming] = useState<MyApiKey | null>(null);
    const [showPrevious, setShowPrevious] = useState(false);

    const eligible = canHoldKey(user);
    const docsUrl = partnerApiDocsUrl();
    const activeKeys = keys.filter(k => k.status === 'active');
    const previousKeys = keys.filter(k => k.status !== 'active');
    // One active key per user. The server refuses a second one whatever this
    // button says; disabling it only spares a dialog that is bound to fail. A
    // list that is loading or failed does not count, the server still decides.
    const settled = !loading && !error;
    const hasActive = settled && activeKeys.length > 0;
    // Only a key that can still be revoked keeps the card open for a user who
    // lost access; revoked ones are history, not a reason to promise anything.
    const notEnabled = !eligible && settled && activeKeys.length === 0;

    const revoke = async (record: MyApiKey) => {
        setConfirming(null);
        setRevoking(true);
        try {
            await revokeMyApiKey(dataProvider, record.id);
            notify(`Revoked ${record.prefix}. Live within 60 seconds.`, {
                type: 'info',
            });
        } catch (e) {
            notify(`Could not revoke: ${String(e)}`, { type: 'error' });
        } finally {
            setRevoking(false);
            refetch();
        }
    };

    const create = async (label: string) => {
        const raw = await createMyApiKey(dataProvider, label);
        refetch();
        return raw;
    };

    return (
        <Card sx={{ mt: 3, p: 3 }}>
            <Stack gap={2}>
                <Typography variant="h6">API access</Typography>

                {notEnabled ? (
                    <Alert severity="info">
                        API access is not enabled for your account. Ask your
                        administrator.
                    </Alert>
                ) : (
                    <>
                        {eligible && (
                            <>
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                >
                                    Call the partner API from your own code.
                                </Typography>
                                {docsUrl && (
                                    <Stack
                                        direction="row"
                                        alignItems="center"
                                        flexWrap="wrap"
                                        gap={1.5}
                                    >
                                        <Button
                                            component="a"
                                            href={docsUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            variant="outlined"
                                            endIcon={<OpenInNewIcon />}
                                        >
                                            Open the API docs
                                        </Button>
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                        >
                                            OpenAPI · Swagger UI
                                        </Typography>
                                    </Stack>
                                )}
                                <Divider />
                            </>
                        )}

                        {!eligible && activeKeys.length > 0 && (
                            <Alert severity="info">
                                API access is no longer enabled for your
                                account. You can still revoke your key.
                            </Alert>
                        )}

                        <Stack
                            direction="row"
                            alignItems="center"
                            justifyContent="space-between"
                        >
                            <Typography variant="subtitle1">
                                Your key
                            </Typography>
                            {eligible && (
                                <Tooltip
                                    title={hasActive ? ACTIVE_KEY_OWN : ''}
                                >
                                    {/* A disabled button fires no events, so the
                                        tooltip hangs on a wrapper. */}
                                    <span>
                                        <Button
                                            size="small"
                                            startIcon={<AddIcon />}
                                            disabled={hasActive}
                                            onClick={() => setCreating(true)}
                                        >
                                            Create key
                                        </Button>
                                    </span>
                                </Tooltip>
                            )}
                        </Stack>

                        {error ? (
                            <Alert
                                severity="error"
                                action={
                                    <Button
                                        color="inherit"
                                        size="small"
                                        onClick={refetch}
                                    >
                                        Retry
                                    </Button>
                                }
                            >
                                Could not load your keys.
                            </Alert>
                        ) : loading ? (
                            <Stack alignItems="center" sx={{ py: 2 }}>
                                <CircularProgress size={20} />
                            </Stack>
                        ) : (
                            <>
                                {activeKeys.length === 0 && eligible && (
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                    >
                                        No active key. Create one to start
                                        calling the API.
                                    </Typography>
                                )}
                                {activeKeys.map(key => (
                                    <KeyRow
                                        key={key.id}
                                        record={key}
                                        disabled={revoking}
                                        onRevoke={() => setConfirming(key)}
                                    />
                                ))}
                                {eligible && hasActive && (
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                    >
                                        One active key at a time. To rotate,
                                        revoke it, then create a new one.
                                    </Typography>
                                )}
                                {previousKeys.length > 0 && (
                                    <>
                                        <Button
                                            size="small"
                                            sx={{ alignSelf: 'flex-start' }}
                                            aria-expanded={showPrevious}
                                            startIcon={
                                                showPrevious ? (
                                                    <ExpandMoreIcon />
                                                ) : (
                                                    <ChevronRightIcon />
                                                )
                                            }
                                            onClick={() =>
                                                setShowPrevious(v => !v)
                                            }
                                        >
                                            Previous keys ({previousKeys.length}
                                            )
                                        </Button>
                                        {showPrevious &&
                                            previousKeys.map(key => (
                                                <KeyRow
                                                    key={key.id}
                                                    record={key}
                                                />
                                            ))}
                                    </>
                                )}
                            </>
                        )}
                    </>
                )}
            </Stack>

            <Dialog
                open={!!confirming}
                onClose={() => setConfirming(null)}
                className={KEEP_DIALOG_SIZE}
            >
                <DialogTitle>Revoke this key?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        <strong>{confirming?.prefix}</strong>
                        {confirming?.label ? ` — ${confirming.label}` : ''} will
                        stop working within 60 seconds. This cannot be undone:
                        the key itself is not recoverable, so restoring access
                        means creating a new key and updating your integration.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirming(null)}>Cancel</Button>
                    <Button
                        color="error"
                        variant="contained"
                        onClick={() => confirming && revoke(confirming)}
                    >
                        Revoke
                    </Button>
                </DialogActions>
            </Dialog>

            <CreateApiKeyDialog
                open={creating}
                onClose={() => {
                    setCreating(false);
                    refetch();
                }}
                onCreate={create}
                activeKeyMessage={ACTIVE_KEY_OWN}
            />
        </Card>
    );
};
